import React, { useEffect, useState, useRef } from "react";
import { useChat } from "../../contexts/ChatProvider";
import { Loading } from "@minchat/react-chat-ui";
import DeleteChatIcon from "../icons/DeleteChatIcon";
import ViewIcon from "../icons/ViewIcon";
import ConfirmationModal from "../modals/ConfirmationModal";
import { useCallback } from "react";
import { hexToRgba } from "../../utils/helper";
import Tooltip from "../ToolTip";
import { useLocale } from "../../contexts/LocaleProvider";
import EmptyChatState from "./EmptySessionView";
import { calculateTimeAgo } from "../../utils/date-utils";

const styles = {
    panel: {
        height: "100%",
        bottom: 0,
        right: 0,
        width: "100%",
        maxWidth: "768px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
        zIndex: 1000,
        position: "absolute",
        backdropFilter: "blur(2px)",
        borderRadius: "8px",
        padding: "8px",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.25)",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 20px",
        borderBottom: "1px solid #ececec",
        flexShrink: 0,
    },
    title: {
        fontSize: "16px",
        fontWeight: 600,
        color: "#1a1a1a",
        margin: 0,
    },
    closeButton: {
        background: "none",
        border: "none",
        cursor: "pointer",
        fontSize: "18px",
        color: "#555555",
        lineHeight: 1,
        padding: "4px",
    },
    body: {
        flex: 1,
        overflowY: "auto",
        width: "100%",
    },
    errorBanner: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        margin: "12px 20px",
        padding: "12px 14px",
        borderRadius: "8px",
        backgroundColor: "#FDECEC",
        border: "1px solid #F5C2C2",
        color: "#9A3B3B",
        fontSize: "13px",
        fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    errorText: {
        flex: 1,
        lineHeight: 1.4,
    },
    errorActions: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexShrink: 0,
    },
    retryButton: {
        background: "none",
        border: "1px solid #9A3B3B",
        borderRadius: "6px",
        padding: "4px 10px",
        fontSize: "12px",
        fontWeight: 600,
        color: "#9A3B3B",
        cursor: "pointer",
    },
    dismissButton: {
        background: "none",
        border: "none",
        cursor: "pointer",
        fontSize: "14px",
        color: "#9A3B3B",
        lineHeight: 1,
        padding: "2px",
    },
    row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 20px",
        borderBottom: "1px solid #ececec",
        cursor: "pointer",
        // borderRadius: "8px",
        transition: "box-shadow 150ms ease, background-color 150ms ease",
    },
    rowHover: {
        // boxShadow: "0 4px 14px rgba(0, 0, 0, 0.12)",
        backgroundColor: "#e7e4e4",
    },
    rowTitle: {
        color: "#1a1a1a",
        fontSize: "14px",
        fontWeight: 400,
        flex: 1,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        marginRight: "16px",
    },
    time: {
        color: "#8a8a8a",
        fontSize: "13px",
        flexShrink: 0,
        textAlign: "right",
    },
    rowActions: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
        flexShrink: 0,
    },
    iconButton: {
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "0px 6px",
        height: 24,
        width: 24,
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "box-shadow 150ms ease, background-color 150ms ease",
    },
    iconButtonHover: {
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
        backgroundColor: "#f0f0f0",
    },
    loadingContainer: {
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1,
        position: "relative",
    },
    loadMoreWrap: {
        display: "flex",
        justifyContent: "center",
        padding: "16px 20px",
        flexShrink: 0
    },
    loadMoreButton: {
        background: "none",
        border: "1px solid #d9d9d9",
        borderRadius: "8px",
        padding: "8px 20px",
        fontSize: "13px",
        fontWeight: 600,
        color: "#333333",
        cursor: "pointer",
    },
    loadMoreButtonDisabled: {
        opacity: 0.6,
        cursor: "default",
    },
};

function ErrorBanner({ message, onRetry, onDismiss }) {
    if (!message) return null;
    return (
        <div style={styles.errorBanner}>
            <span style={styles.errorText}>{message}</span>
        </div>
    );
}

export default function ChatSessionPanel() {
    const {
        shoji,
        sessions,
        getPreviousChats,
        theme,
        setShowChatList,
        setSessionListIndex,
        setSessionId,
        setLoading,
        fetchConversations,
        setMessages,
        setDisableInput,
        setStartIndex,
        disableMoreSessions,
        deleteChat,
        sessionId,
        activeSessionIdRef,
        widgetLanguage,
        setShowTypingIndicator,
        showTypingIndicator,
        setSessionSwitch
    } = useChat();
    const { t } = useLocale();
    const [sessionLoading, setSessionLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [hoveredIcon, setHoveredIcon] = useState(null);
    const [sessionToDelete, setSessionToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [listError, setListError] = useState(null);
    const [deleteError, setDeleteError] = useState(false);
    const [deleteSuccess, setDeleteSuccess] = useState(false);
    const pageIndexRef = useRef(0);

    const loadSessions = ({ isInitial = false, isLoadMore = false } = {}) => {
        setListError(null);
        if (isInitial) setSessionLoading(true);
        if (isLoadMore) setLoadingMore(true);

        return getPreviousChats()
            .then(() => {
                if (isInitial) setSessionLoading(false);
                if (isLoadMore) setLoadingMore(false);
            })
            .catch(() => {
                if (isInitial) setSessionLoading(false);
                if (isLoadMore) setLoadingMore(false);
                setListError(
                    isLoadMore
                        ? t("sessionList.errors.loadMore")
                        : t("sessionList.errors.loadInitial")
                );
            });
    };

    const handleSessionClick = (session) => {
        setListError(null);
        setSessionId(session?.session_id);
        activeSessionIdRef.current = session?.session_id;
        setDisableInput(false);
        setStartIndex(0);
        setShowChatList(false);
        setLoading(true);
        setSessionSwitch(true);
        if (session?.session_status === "finished") setDisableInput(true);
        setMessages([]);
        setShowTypingIndicator(false);
        fetchConversations(session?.session_id, true)
            .then(() => setLoading(false))
            .catch(() => setLoading(false))
            .finally(() => { setSessionSwitch(false)});
    };

    const handleLoadMore = () => {
        if (loadingMore) return;
        pageIndexRef.current += 1;
        setSessionListIndex(pageIndexRef.current);
        loadSessions({ isLoadMore: true });
    };

    const handleRetryList = () => {
        if (sessionLoading) return;
        loadSessions({ isInitial: sessions.length === 0 });
    };

    const handleConfirmDelete = () => {
        if (!sessionToDelete || deleting) return;
        setDeleting(true);
        setDeleteError(false);
        deleteChat(sessionToDelete.session_id)
            .then(() => {
                setDeleteSuccess(true);
                setDeleting(false);
            })
            .catch(() => {
                setDeleteError(true);
                setDeleting(false);
            })
    };

    useEffect(() => {
        if (sessions && sessions.length > 0) {
            setSessionLoading(false);
            return;
        }

        pageIndexRef.current = 0;
        setSessionListIndex(0);
        loadSessions({ isInitial: true });
    }, []);

    return (
        <div style={styles.panel}>
            <div style={styles.header}>
                <h2 style={styles.title}>{t("sessionList.header")}</h2>
                <button
                    type="button"
                    style={styles.closeButton}
                    onClick={() => { setShowChatList(false) }}
                    aria-label={t("common.close")}
                >
                    ✕
                </button>
            </div>

            <ErrorBanner
                message={listError}
                onRetry={handleRetryList}
                onDismiss={() => setListError(null)}
            />

            <div style={styles.body}>
                {sessionLoading ? (
                    <div style={styles.loadingContainer}>
                        <Loading themeColor={theme} />
                    </div>
                ) : (
                    <>
                        {sessions?.length > 0 ? sessions.map((session, index) => {
                            const rowKey = session.id || index;
                            const isRowHovered = hoveredRow === rowKey;
                            const isActive = session.session_id === sessionId;
                            return (
                                <div
                                    key={rowKey}
                                    style={{
                                        ...styles.row,
                                        ...(isRowHovered ? styles.rowHover : {}),
                                        ...(isActive ? { backgroundColor: hexToRgba(theme, 0.1) } : {}),
                                        ...(showTypingIndicator ? { "cursor" : "wait" } : { "cursor" : "pointer"})
                                    }}
                                    onMouseEnter={() => setHoveredRow(rowKey)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                    onClick={() => handleSessionClick(session)}
                                >
                                    <span
                                        style={{
                                            ...styles.rowTitle,
                                            fontWeight: isActive ? 600 : 400,
                                        }}
                                    >
                                        {session.session_title || t("sessionList.untitled")}
                                    </span>
                                    {isRowHovered ? <div style={styles.rowActions}>
                                        <Tooltip text={t("sessionList.actions.view")}>
                                            <button
                                                type="button"
                                                aria-label="View chat"
                                                disabled={showTypingIndicator}
                                                style={{
                                                    ...styles.iconButton,
                                                    ...(hoveredIcon === `${rowKey}-view` ? styles.iconButtonHover : {}),
                                                    ...(showTypingIndicator ? { "cursor" : "wait" } : { "cursor" : "pointer"})
                                                }}
                                                onMouseEnter={() => setHoveredIcon(`${rowKey}-view`)}
                                                onMouseLeave={() => setHoveredIcon(null)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSessionClick(session);
                                                }}
                                            >
                                                <ViewIcon />
                                            </button>
                                        </Tooltip>
                                        <Tooltip text={t("sessionList.actions.delete")} >
                                            <button
                                                type="button"
                                                aria-label="Delete chat"
                                                disabled={showTypingIndicator}
                                                style={{
                                                    ...styles.iconButton,
                                                    ...(hoveredIcon === `${rowKey}-delete` ? styles.iconButtonHover : {}),
                                                    ...(showTypingIndicator ? { "cursor" : "wait" } : { "cursor" : "pointer"})
                                                }}
                                                onMouseEnter={() => setHoveredIcon(`${rowKey}-delete`)}
                                                onMouseLeave={() => setHoveredIcon(null)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDeleteError(null);
                                                    setSessionToDelete(session);
                                                }}
                                            >
                                                <DeleteChatIcon color="red" />
                                            </button>
                                        </Tooltip>
                                    </div> :
                                        <span style={styles.time}>
                                            {calculateTimeAgo(new Date(session.created_at), shoji(), widgetLanguage)}
                                        </span>}
                                </div>
                            );
                        }): ( !listError && <EmptyChatState />)}
                    </>
                )}
            </div>
            {!(disableMoreSessions || listError || sessionLoading) && <div style={styles.loadMoreWrap}>
                <button
                    type="button"
                    style={{
                        ...styles.loadMoreButton,
                        ...(loadingMore ? styles.loadMoreButtonDisabled : {}),
                    }}
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                >
                    {loadingMore ? t("sessionList.loading") : t("sessionList.loadMore")}
                </button>
            </div>}

            <ConfirmationModal
                isOpen={!!sessionToDelete}
                title={!(deleteError || deleteSuccess) ? t("deleteChatModal.title") : deleteSuccess ? t("deleteChatResult.success.title") : t("deleteChatResult.failure.title")}
                message={
                    deleteError
                        ? t("deleteChatResult.failure.message")
                        : deleteSuccess
                            ? t("deleteChatResult.success.message")
                            : t("deleteChatModal.message")
                }
                confirmText={deleting ? t("deleteChatModal.confirmButtonLoading") : t("deleteChatModal.confirmButton")}
                disableConfirm={deleting}
                cancelText={t("common.cancel")}
                destructive
                onConfirm={handleConfirmDelete}
                onCancel={() => {
                    setSessionToDelete(null);
                    setDeleteError(false);
                    setDeleteSuccess(false)
                }}
                error={deleteError}
                success={deleteSuccess}
                showCloseButton={deleteError || deleteSuccess}
            />
        </div>
    );
}